import { useCallback, useState } from 'react';
import { Button } from '@material-ui/core';
import cx from 'classnames';
import isEqual from 'lodash/isEqual';
import { useDebouncedCallback } from 'use-debounce';

import { CsvUpload } from 'src/components/CsvUpload';
import { Loader } from 'src/components/Loader';
import { PROMPT_INSTRUCTIONS_ACTION, PromptInstructions } from 'src/components/PromptInstructions';
import { replaceFilePlaceholders } from 'src/components/RichSlate/lib';
import { StaticPanel } from 'src/components/StaticPanel';
import { getMultiPromptFields } from 'src/data/generationTool';
import { getCreatedResourceMessage } from 'src/data/performance';
import { TEMPLATE_BUILDER_PARAMS } from 'src/data/templates';
import { EVENT_PAGE } from 'src/lib/events';
import { ROUTE_PARAMS, ROUTES } from 'src/routes';
import { useBrandBriefsMenuItems, useHistoryPushOrInfinityOpen } from 'src/services';
import { usePreparePromptInstructionsProps } from 'src/services/prompts';
import { useBrandVoicesAssets, useNotificationActions } from 'src/store';
import { useMultiPromptTemplateActions } from 'src/store/multiPromptTemplates';
import { BlockBrandVocabulary } from 'src/views/ProjectPage/views/BlockBrandVocabulary';

import { useLocalActions } from '../../store';
import { BlockPersonas } from '../BlockPersonas';

import { TextArea } from './components/TextArea';
import { useEvents } from './useEvents';
import { useStyles } from './useStyles';
import { useViewState } from './useViewState';

export const BlockMultiPrompt = ({
  inGenerationPanel,
  onChangeSettings,
  className,
  onEdit,
  customTemplateId,
}) => {
  const styles = useStyles();
  const localActions = useLocalActions();
  const notificationActions = useNotificationActions();
  const historyPushOrInfinityOpen = useHistoryPushOrInfinityOpen();
  const multiPromptTemplateActions = useMultiPromptTemplateActions();
  const [plusButtonRef, setPlusButtonRef] = useState(false);
  const [uploadingImageIndex, setUploadingImageIndex] = useState(null);
  const [uploadingDocumentIndex, setUploadingDocumentIndex] = useState(null);
  const {
    multiPromptFields,
    generationTool,
    generating,
    multiPromptFieldImages,
    multiPromptFieldDocuments,
    promptImages,
    promptDocuments,
    currentUrls,
    uploadingDocuments,
    uploadingImages,
    uploadedCsv,
    isCsvLoading,
    language,
  } = useViewState();

  const multiPromptFieldData = customTemplateId
    ? multiPromptTemplateActions.getMultiPromptTemplateFields(customTemplateId)
    : getMultiPromptFields(generationTool);
  const multiPromptTemplate = customTemplateId
    ? multiPromptTemplateActions.getMultiPromptTemplate(customTemplateId)
    : undefined;
  const isCsvTemplate = multiPromptTemplate?.templateType === TEMPLATE_BUILDER_PARAMS.csv;

  const handlePromptInstructionsAction = useCallback(
    (action, data) => {
      switch (action) {
        case PROMPT_INSTRUCTIONS_ACTION.insertImage: {
          localActions.setMultiPromptFieldImages(data.item, data.randomId, data.index);
          const { id, ...restProps } = data.item;
          if (data.randomId) {
            localActions.addPromptImage(id, restProps, data.randomId);
          } else {
            if (!Object.keys(promptImages).includes(id)) {
              localActions.addPromptImage(id, restProps);
            }
          }
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.insertDocument: {
          localActions.setMultiPromptFieldDocuments(data.item, null, data.randomId, data.index);
          const { id, ...restProps } = document;
          if (data.randomId) {
            localActions.addPromptDocument(id, restProps, data.randomId);
          } else {
            if (!Object.keys(promptDocuments).includes(id)) {
              localActions.addPromptDocument(id, restProps);
            }
          }
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.removePromptImage: {
          localActions.removeIdFromMultiPromptFieldImages(data.itemId, data.index);
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.removeUploadingImage: {
          localActions.removeUploadingImage(data.itemId);
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.removePromptDocument: {
          localActions.removeIdFromMultiPromptFieldDocuments(data.itemId, data.index);
          break;
        }

        case PROMPT_INSTRUCTIONS_ACTION.removeUploadingDocument: {
          localActions.removeUploadingDocument(data.itemId);
          break;
        }

        default:
          break;
      }
    },
    [localActions, promptDocuments, promptImages]
  );

  const setIsUploadingImage = useCallback(
    (isUploadingImage, randomId, index) => {
      localActions.setIsUploadingImage(isUploadingImage);
      setUploadingImageIndex(index);
      if (isUploadingImage && randomId) {
        localActions.addUploadingImage(randomId);
        localActions.setMultiPromptFieldImages(null, randomId, index);
      }
    },
    [localActions]
  );

  const setIsUploadingDocument = useCallback(
    (isUploadingDocument, name, randomId, index) => {
      localActions.setIsUploadingDocument(isUploadingDocument);
      setUploadingDocumentIndex(index);
      if (isUploadingDocument && randomId) {
        localActions.addUploadingDocument(randomId, name);
        localActions.setMultiPromptFieldDocuments(null, name, randomId, index);
      }
    },
    [localActions]
  );

  const setDocumentError = useCallback(
    (id, error) => {
      localActions.updatePromptDocumentError(id, error);
      notificationActions.displayError(error);
    },
    [localActions, notificationActions]
  );

  const setImageError = useCallback(
    (id, error) => {
      localActions.updatePromptImageErrors(id, error);
      notificationActions.displayError(error);
    },
    [localActions, notificationActions]
  );

  const setImageUploadSuccess = useCallback(
    (image) => {
      notificationActions.displaySuccess(getCreatedResourceMessage(image));
    },
    [notificationActions]
  );

  const setDocumentUploadSuccess = useCallback(
    (document) => {
      notificationActions.displaySuccess(getCreatedResourceMessage(document));
    },
    [notificationActions]
  );

  const clearImageError = useCallback(() => {
    localActions.clearPromptImageErrors();
  }, [localActions]);

  const clearDocumentError = useCallback(() => {
    localActions.clearPromptDocumentErrors();
  }, [localActions]);

  const promptInstructionsProps = usePreparePromptInstructionsProps({
    eventPage: EVENT_PAGE.editor,
    generationTool,
    uploadingImages,
    uploadingDocuments,
    currentUrls,
    provideUrlResources: true,
    provideDocumentResources: true,
    provideImageResources: true,
    setIsUploadingDocument,
    setIsUploadingImage,
    setDocumentError,
    setImageError,
    clearDocumentError,
    clearImageError,
    setDocumentUploadSuccess,
    setImageUploadSuccess,
    onAction: handlePromptInstructionsAction,
  });

  const events = useEvents();

  const shouldExtractBriefFromUrlDebounced = useDebouncedCallback(() => {
    localActions.shouldExtractUrlBrief();
  }, 350);

  const handleSettingsChange = useCallback(
    (vars) => {
      onChangeSettings(vars);
      events.trackMultiPromptChangeBrief();
    },
    [onChangeSettings, events]
  );

  const handleLastPromptUrl = useCallback(() => {
    events.trackResourceInserted();
  }, [events]);

  const { brandVoicesAssets, loadingBrandVoiceAssets } = useBrandVoicesAssets();

  const handleEditClick = useCallback(
    (type = 'messaging') => {
      if (type === 'resources') {
        historyPushOrInfinityOpen(`${ROUTES.INTEGRATIONS_RESOURCES}/?${ROUTE_PARAMS.resources}`);
      } else if (type === 'messaging') {
        historyPushOrInfinityOpen(
          `${ROUTES.BRAND_VOICE}/${brandVoicesAssets[0].id}${ROUTES.BRAND_BRIEFS}`
        );
      }
    },
    [brandVoicesAssets, historyPushOrInfinityOpen]
  );

  const brandBriefsMenuItems = useBrandBriefsMenuItems({
    brandVoicesAssets,
    isLoading: loadingBrandVoiceAssets,
    onEditBrief: useCallback(() => {
      handleEditClick('messaging');
    }, [handleEditClick]),
  });

  if (!multiPromptFields || Object.keys(multiPromptFields).length === 0) {
    if (multiPromptFieldData) {
      localActions.initMultiPromptFields(multiPromptFieldData);
    }
    return <Loader className={styles.loader} />;
  }

  const handleDrop = (csvFile) => {
    if (onEdit && !isEqual(csvFile, uploadedCsv)) {
      onEdit();
    }
    localActions.uploadCsv(csvFile);
  };

  const handleDelete = () => {
    localActions.setUploadedCsv(null);
  };

  return (
    <StaticPanel className={styles.wrapper}>
      <div className={cx(styles.root, className)}>
        {inGenerationPanel && (
          <div className={styles.title}>
            <div>Brief</div>

            <Button
              className={cx(styles.button, styles.grayButton)}
              onClick={handleSettingsChange}
              variant="text"
              color="primary"
              disabled={generating}
            >
              Change brief
            </Button>
          </div>
        )}
        {isCsvTemplate ? (
          <div className={cx({ [styles.csvUpload]: !inGenerationPanel || uploadedCsv })}>
            <CsvUpload
              csvFile={uploadedCsv}
              onDelete={handleDelete}
              onDrop={handleDrop}
              isLoading={isCsvLoading}
              hideEmptyState={inGenerationPanel && !uploadedCsv}
              displayOnly={inGenerationPanel}
            />
          </div>
        ) : (
          multiPromptFieldData?.order?.map((field, index) => {
            const fieldHtml = multiPromptFields[field]?.html
              ? multiPromptFields[field].html
              : multiPromptFields[field];
            let fieldSerialized = multiPromptFields[field]?.text
              ? multiPromptFields[field].text
              : '';
            if (
              multiPromptFields[field]?.placeholders &&
              Object.keys(multiPromptFields[field].placeholders).length
            ) {
              fieldSerialized = replaceFilePlaceholders(fieldSerialized, multiPromptFields, field);
            }

            return inGenerationPanel ? (
              <div className={styles.briefRowGenPanel}>
                <TextArea
                  title={multiPromptFieldData.titles[field]}
                  body={fieldSerialized}
                  disabled={inGenerationPanel}
                />
              </div>
            ) : (
              <>
                <div className={styles.fieldTitle}>{multiPromptFieldData.titles[field]}</div>
                <PromptInstructions
                  className={styles.promptField}
                  classes={{ input_root: styles.textArea }}
                  editableClasses={{ editable: styles.editable }}
                  {...promptInstructionsProps}
                  value={fieldHtml}
                  placeHolder={multiPromptFieldData.placeholders[field]}
                  onChange={(name, value) => {
                    if (onEdit && !isEqual(value, localActions.getMultiPromptField(field))) {
                      onEdit();
                    }
                    localActions.setMultiPromptField(field, { ...value, index });
                    shouldExtractBriefFromUrlDebounced();
                  }}
                  brandBriefsMenuItems={brandBriefsMenuItems}
                  generationTool={generationTool}
                  onLastPromptUrl={handleLastPromptUrl}
                  isFirstField={index === 0}
                  hideActions
                  shortExpanding
                  noTags
                  isSavePopup
                  plusButtonRef={plusButtonRef}
                  setPlusButtonRef={setPlusButtonRef}
                  promptImages={promptImages}
                  isUploadingImage={uploadingImageIndex === index}
                  isUploadingDocument={uploadingDocumentIndex === index}
                  promptDocuments={promptDocuments}
                  index={index}
                  disableAutoFocus
                  uploadingImages={multiPromptFieldImages[index]}
                  uploadingDocuments={multiPromptFieldDocuments[index]}
                />
              </>
            );
          })
        )}
        <BlockPersonas
          visibleCount={5}
          disabledCollapsed={inGenerationPanel}
          className={inGenerationPanel ? styles.rowPanel : styles.rowPersonas}
          titleBold={!inGenerationPanel}
          generationTool={generationTool}
          language={language}
        />
        <BlockBrandVocabulary
          className={styles.rowBrandVocabulary}
          disabled={inGenerationPanel}
          language={language}
        />
      </div>
    </StaticPanel>
  );
};
